import { FC, ReactNode, useState } from 'react';
import { Button, Space } from 'antd';
import { TbCopy, TbEye, TbEyeOff } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { SmallTooltip } from '@/shared/components/tooltips';

interface CredentialActionGroupProps {
    children?: ReactNode;
    copyMessage: string;
    initialValue?: string;
    maskedValue: string;
    onReveal?: () => Promise<string>;
}

const CredentialActionGroup: FC<CredentialActionGroupProps> = ({
    children,
    copyMessage,
    initialValue,
    maskedValue,
    onReveal,
}) => {
    const [fullValue, setFullValue] = useState(initialValue);
    const [visible, setVisible] = useState(false);
    const [revealing, setRevealing] = useState(false);

    const toggleVisibility = async () => {
        if (visible) {
            setVisible(false);
            return;
        }

        if (!fullValue && onReveal) {
            try {
                setRevealing(true);
                setFullValue(await onReveal());
            } catch {
                toast.error('Unable to reveal credential');
                return;
            } finally {
                setRevealing(false);
            }
        }

        setVisible(true);
    };

    const displayedValue = visible && fullValue ? fullValue : maskedValue;

    return (
        <Space.Compact>
            <Button size='small' style={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                <code>{displayedValue}</code>
            </Button>
            <SmallTooltip title={visible ? 'Hide' : 'Reveal'}>
                <Button
                    aria-label={visible ? 'Hide credential' : 'Reveal credential'}
                    size='small'
                    loading={revealing}
                    icon={visible ? <TbEyeOff /> : <TbEye />}
                    onClick={() => void toggleVisibility()}
                />
            </SmallTooltip>
            <SmallTooltip title='Copy'>
                <Button
                    aria-label='Copy credential'
                    size='small'
                    disabled={!visible || !fullValue}
                    icon={<TbCopy />}
                    onClick={() => {
                        if (!fullValue) {
                            return;
                        }
                        void navigator.clipboard.writeText(fullValue);
                        toast(copyMessage);
                    }}
                />
            </SmallTooltip>
            {children}
        </Space.Compact>
    );
};

export default CredentialActionGroup;
